import React, { useState, useEffect } from 'react';
import './EmployeesHome.css';

// Icons
const SearchIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <circle cx="11" cy="11" r="8" />
    <path d="M21 21l-4.35-4.35" />
  </svg>
);

const PlusIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M12 5v14M5 12h14" />
  </svg>
);

const FilterIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M4 6h16M6 12h12M8 18h8" />
  </svg>
);

const ViewIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
    <circle cx="12" cy="12" r="3" />
  </svg>
);

const EditIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7" />
    <path d="M18.5 2.5a2.121 2.121 0 013 3L12 15l-4 1 1-4 9.5-9.5z" />
  </svg>
);

const DeleteIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M3 6h18M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2" />
  </svg>
);

const ChevronDownIcon = () => (
  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M6 9l6 6 6-6" />
  </svg>
);

const KebabIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
    <circle cx="12" cy="5" r="2" />
    <circle cx="12" cy="12" r="2" />
    <circle cx="12" cy="19" r="2" />
  </svg>
);

// Generate avatar from name
const getAvatarUrl = (name) => {
  return `https://ui-avatars.com/api/?name=${encodeURIComponent(name)}&background=4F46E5&color=fff&size=100`;
};

const EmployeesHome = ({ onAddEmployee }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [activeTab, setActiveTab] = useState('active');
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [employeesData, setEmployeesData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [openMenuId, setOpenMenuId] = useState(null);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = () => setOpenMenuId(null);
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  const toggleMenu = (e, employeeId) => {
    e.stopPropagation();
    setOpenMenuId(openMenuId === employeeId ? null : employeeId);
  };

  const handleMenuAction = (action, employee) => {
    setOpenMenuId(null);
    // Handle actions here
    console.log(action, employee);
  };

  // Fetch employees data from API
  useEffect(() => {
    const fetchEmployees = async () => {
      setIsLoading(true);
      setError('');
      
      try {
        const token = localStorage.getItem('authToken');
        const response = await fetch(
          'https://motobot.in/ttessapi/v1/employeeprofile/emp_profile/index/getall?EmploymentStatusId=All',
          {
            method: 'GET',
            headers: {
              'Content-Type': 'application/json',
              ...(token && { 'Authorization': `Bearer ${token}` })
            }
          }
        );

        const data = await response.json();

        if (data.success) {
          setEmployeesData(data.data || []);
        } else {
          setError(data.message || 'Failed to fetch employees');
        }
      } catch (err) {
        setError('Network error. Please check your connection.');
      } finally {
        setIsLoading(false);
      }
    };

    fetchEmployees();
  }, []);

  // Filter employees based on active tab and search query
  const filteredEmployees = employeesData.filter(emp => {
    const matchesTab = activeTab === 'active' 
      ? emp.employmentStatusName === 'Active'
      : emp.employmentStatusName === 'Inactive';
    
    const matchesSearch = searchQuery.trim() === '' || 
      emp.fullName?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      emp.employeeId?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      emp.emailId?.toLowerCase().includes(searchQuery.toLowerCase());
    
    return matchesTab && matchesSearch;
  });

  // Pagination
  const totalRecords = filteredEmployees.length;
  const totalPages = Math.ceil(totalRecords / rowsPerPage);
  const startIndex = (currentPage - 1) * rowsPerPage;
  const paginatedEmployees = filteredEmployees.slice(startIndex, startIndex + rowsPerPage);

  // Counts
  const activeCount = employeesData.filter(emp => emp.employmentStatusName === 'Active').length;
  const inactiveCount = employeesData.filter(emp => emp.employmentStatusName === 'Inactive').length;

  return (
    <div className="employees-home">
      {/* Top Actions Bar */}
      <div className="employees-actions-bar">
        <div className="employees-search">
          <SearchIcon />
          <input
            type="text"
            placeholder="Search here"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="employees-search-input"
          />
        </div>

        <div className="employees-actions-right">
          <button className="add-employee-btn" onClick={onAddEmployee}>
            <PlusIcon />
            <span>Add New Employee</span>
          </button>
          <button className="filter-btn">
            <FilterIcon />
            <span>Filter</span>
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="employees-tabs">
        <button
          className={`tab-btn ${activeTab === 'active' ? 'active' : ''}`}
          onClick={() => setActiveTab('active')}
        >
          Active
          <span className="tab-count">{activeCount}</span>
        </button>
        <button
          className={`tab-btn ${activeTab === 'inactive' ? 'active' : ''}`}
          onClick={() => setActiveTab('inactive')}
        >
          Inactive
          <span className="tab-count inactive">{inactiveCount}</span>
        </button>
      </div>

      {/* Table */}
      <div className="employees-table-container">
        {isLoading ? (
          <div className="loading-container">
            <div className="loading-spinner"></div>
            <p>Loading employees...</p>
          </div>
        ) : error ? (
          <div className="error-container">
            <p>{error}</p>
          </div>
        ) : (
          <table className="employees-table">
            <thead>
              <tr>
                <th>Serial No.</th>
                <th>Employee Id</th>
                <th>Employee Name</th>
                <th>Status</th>
                <th>Action</th>
              </tr>
            </thead>
            <tbody>
              {paginatedEmployees.length === 0 ? (
                <tr>
                  <td colSpan="5" className="no-data">No employees found</td>
                </tr>
              ) : (
                paginatedEmployees.map((employee, index) => (
                  <tr key={employee.empProfileIndexId}>
                    <td>{String(startIndex + index + 1).padStart(2, '0')}</td>
                    <td>{employee.employeeId}</td>
                    <td>
                      <div className="employee-name-cell">
                        <img
                          src={getAvatarUrl(employee.fullName)}
                          alt={employee.fullName}
                          className="employee-avatar"
                        />
                        <span>{employee.fullName}</span>
                      </div>
                    </td>
                    <td>
                      <span className={`status-badge ${employee.employmentStatusName?.toLowerCase()}`}>
                        <span className="status-dot"></span>
                        {employee.employmentStatusName}
                      </span>
                    </td>
                    <td>
                      <div className="action-buttons">
                        <button className="action-btn view" title="View">
                          <ViewIcon />
                        </button>
                        <button className="action-btn edit" title="Edit">
                          <EditIcon />
                        </button>
                        <button className="action-btn delete" title="Delete">
                          <DeleteIcon />
                        </button>
                        <div className="kebab-menu-container">
                          <button 
                            className="action-btn kebab" 
                            title="More"
                            onClick={(e) => toggleMenu(e, employee.empProfileIndexId)}
                          >
                            <KebabIcon />
                          </button>
                          {openMenuId === employee.empProfileIndexId && (
                            <div className="kebab-dropdown">
                              <button onClick={() => handleMenuAction('view', employee)}>
                                <ViewIcon /> View Details
                              </button>
                              <button onClick={() => handleMenuAction('edit', employee)}>
                                <EditIcon /> Edit Employee
                              </button>
                              <button onClick={() => handleMenuAction('deactivate', employee)}>
                                <DeleteIcon /> Deactivate
                              </button>
                              <button onClick={() => handleMenuAction('documents', employee)}>
                                <ViewIcon /> Documents
                              </button>
                              <button onClick={() => handleMenuAction('compensation', employee)}>
                                <EditIcon /> Compensation
                              </button>
                            </div>
                          )}
                        </div>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      <div className="employees-pagination">
        <div className="pagination-left">
          <span>Showing</span>
          <div className="rows-select">
            <select
              value={rowsPerPage}
              onChange={(e) => {
                setRowsPerPage(Number(e.target.value));
                setCurrentPage(1);
              }}
            >
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
            </select>
            <ChevronDownIcon />
          </div>
        </div>

        <div className="pagination-info">
          Showing {totalRecords === 0 ? 0 : startIndex + 1} to {Math.min(startIndex + rowsPerPage, totalRecords)} Out of {totalRecords} records
        </div>

        <div className="pagination-right">
          <button 
            className="page-btn" 
            disabled={currentPage === 1}
            onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
          >
            &lt;
          </button>
          {Array.from({ length: Math.min(totalPages, 4) }, (_, i) => i + 1).map((page) => (
            <button
              key={page}
              className={`page-btn ${currentPage === page ? 'active' : ''}`}
              onClick={() => setCurrentPage(page)}
            >
              {page}
            </button>
          ))}
          <button 
            className="page-btn"
            disabled={currentPage === totalPages || totalPages === 0}
            onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
          >
            &gt;
          </button>
        </div>
      </div>
    </div>
  );
};

export default EmployeesHome;

